import {
    DirectSubChannel,
    digitalChannelMode,
    directChannelMode,
    FixedSensorPort,
    OpenSensorPort,
    SensorPortCommunicationMode,
    sdi12ChannelMode,
    unavailableChannelMode,
} from "./portConfigs.js";

// ─── Port template types (portId is provided by the device config) ─────────────

type OpenSensorPortTemplate = Omit<OpenSensorPort, "portId">;
type FixedSensorPortTemplate = Omit<FixedSensorPort, "portId">;

// ─── Channel mode helpers ─────────────────────────────────────────────────────

/** Channel mode map with only DIRECT available; all others unavailable. */
const directOnly = (subChannels: DirectSubChannel[]) =>
    ({
        [SensorPortCommunicationMode.DIRECT]: directChannelMode(subChannels),
        [SensorPortCommunicationMode.DIGITAL]: unavailableChannelMode,
        [SensorPortCommunicationMode.SDI12]: unavailableChannelMode,
        [SensorPortCommunicationMode.RS485]: unavailableChannelMode,
    }) as const;

/** Channel mode map with only SDI-12 available; all others unavailable. */
const sdi12Only = (maxChannels = 10) =>
    ({
        [SensorPortCommunicationMode.DIRECT]: unavailableChannelMode,
        [SensorPortCommunicationMode.DIGITAL]: unavailableChannelMode,
        [SensorPortCommunicationMode.SDI12]: sdi12ChannelMode(maxChannels),
        [SensorPortCommunicationMode.RS485]: unavailableChannelMode,
    }) as const;

/** Channel mode map with only DIGITAL available; all others unavailable. */
const digitalOnly = {
    [SensorPortCommunicationMode.DIRECT]: unavailableChannelMode,
    [SensorPortCommunicationMode.DIGITAL]: digitalChannelMode(),
    [SensorPortCommunicationMode.SDI12]: unavailableChannelMode,
    [SensorPortCommunicationMode.RS485]: unavailableChannelMode,
} as const;

// ─── Template factories ───────────────────────────────────────────────────────

/** Template for a fixed (non-user selectable) port.
 * `isVirtual` marks computed/aggregated data;
 * `isInternal` marks device-diagnostic data not shown to end users.
 * */
function fixedPortTemplate(
    isVirtual: boolean,
    isInternal: boolean,
    sensorClusterToPopulateWith: string,
): FixedSensorPortTemplate {
    return {
        isFixed: true,
        isVirtual,
        isInternal,
        sensorClusterToPopulateWith,
        channelRangeByCommunicationMode: digitalOnly,
    };
}

/** Template for a user-wired open port supporting the given direct sub-channels. */
function openDirectPortTemplate(subChannels: DirectSubChannel[]): OpenSensorPortTemplate {
    return {
        isFixed: false,
        channelRangeByCommunicationMode: directOnly(subChannels),
    };
}

// ─── Port Catalogue ───────────────────────────────────────────────────────────
//
// Each entry is a portId-free template. Device configs spread the template and
// supply portId at the assignment site:
//
//   S1: { portId: "S1", ...portDefinitionCatalogue.gen4_S_adc }
//
// This lets identical-capability ports (e.g. both Sprout sensor ports) share one
// template without encoding a specific slot number in the name.

export const portDefinitionCatalogue = {
    // ── Shared diagnostic/valve ports (identical across all device generations) ──

    /** Firmware crash counter. */
    D_crashCount: { ...fixedPortTemplate(true, true, "CrashCount"), visibility: "adminOnly" },
    /** Firmware reset counter. */
    D_resetCount: { ...fixedPortTemplate(true, true, "ResetCount"), visibility: "adminOnly" },
    /** Device output state and operating mode. Drives a solenoid valve or a latching relay. */
    O_output: fixedPortTemplate(true, false, "Output"),

    // ── Gen4 open sensor ports ────────────────────────────────────────────────

    /** Gen4 ADC input (pressure transducers and voltage sensors — 5 V and 12 V variants). */
    gen4_S_adc: openDirectPortTemplate([DirectSubChannel.ADC_5V, DirectSubChannel.ADC_12V]),
    /** Gen4 PWM input with totalization (flowmeters). */
    gen4_S_pwm: openDirectPortTemplate([DirectSubChannel.PULSE_FREQUENCY, DirectSubChannel.TOTAL]),
    /** Gen4 pulse-total-only input (dedicated totalizer port). */
    gen4_S_total: openDirectPortTemplate([DirectSubChannel.TOTAL]),

    // ── Gen4 fixed/diagnostic ports ───────────────────────────────────────────

    /** Gen4 onboard temperature and humidity sensor. */
    gen4_I_tempHum: fixedPortTemplate(false, true, "Gen4_TempHum"),
    /** Gen4 last firmware error code. */
    gen4_D_lastErrorCode: { ...fixedPortTemplate(true, true, "Gen4_LastErrorCode"), visibility: "adminOnly" },
    /** Gen4 LoRa network statistics (packet loss, frame count). */
    gen4_D_networkStats: fixedPortTemplate(true, true, "Gen4_NetworkStats"),
    /** Gen4 battery voltage. */
    gen4_D_battery: fixedPortTemplate(true, true, "Gen4_Battery"),
    /** Gen4 device output state, operating mode, and solenoid actuation verification. */
    gen4_O_output: fixedPortTemplate(true, false, "Gen4_Output"),

    // ── Sprout (gen3) open sensor ports ──────────────────────────────────────

    /** Sprout ADC_5V and PWM input (no totalization). Used for both S1 and S2. */
    sprout_S: openDirectPortTemplate([DirectSubChannel.ADC_5V, DirectSubChannel.PULSE_FREQUENCY]),

    // ── Sprout fixed/diagnostic ports ─────────────────────────────────────────

    /** Sprout onboard temperature and humidity sensor. */
    sprout_I_tempHum: fixedPortTemplate(false, true, "Sprout_TempHum"),
    /** Sprout LoRa network statistics. */
    sprout_D_networkStats: fixedPortTemplate(true, true, "Sprout_NetworkStats"),
    /** Sprout battery voltage. */
    sprout_D_battery: fixedPortTemplate(true, true, "Sprout_Battery"),

    // ── Seed / Sapling (retired generations) open sensor ports ───────────────

    /** Seed/Sapling ADC_5V and PWM input (no totalization). Used for both S1 and S2. */
    seedSap_S: openDirectPortTemplate([DirectSubChannel.ADC_5V, DirectSubChannel.PULSE_FREQUENCY]),

    // ── Seed / Sapling fixed/diagnostic ports ────────────────────────────────

    /** Seed/Sapling onboard temperature and humidity sensor. */
    seedSap_I_tempHum: fixedPortTemplate(false, true, "SeedSap_TempHum"),
    /** Seed/Sapling LoRa network statistics. */
    seedSap_D_networkStats: fixedPortTemplate(true, true, "SeedSap_NetworkStats"),
    /** Seed/Sapling battery voltage. */
    seedSap_D_battery: fixedPortTemplate(true, true, "SeedSap_Battery"),
    /** Sapling solenoid actuation verification. */
    seedSap_D_saplingVerification: fixedPortTemplate(true, true, "SeedSap_SaplingVerification"),

    // ── Dragino SDI-12 node ports ─────────────────────────────────────────────

    /** Dragino SDI-12 node sensor port — intentionally limited to one configured channel. */
    draginoSDI12_S: {
        isFixed: false,
        channelRangeByCommunicationMode: sdi12Only(1),
    } satisfies OpenSensorPortTemplate,
    /** Dragino SDI-12 node battery. */
    draginoSDI12_D_battery: fixedPortTemplate(true, true, "DraginoSDI12_Battery"),

    // ── Sensoterra probe ports ────────────────────────────────────────────────

    /** Sensoterra Single Depth Sensor soil moisture port. */
    sensoterra_S: fixedPortTemplate(false, false, "sensoterra_single_depth"),
    /** Sensoterra probe battery. */
    sensoterra_D_battery: fixedPortTemplate(true, true, "Sensoterra_Battery"),

    // ── ePlant TreeTag ports ───────────────────────────────────────────────────

    /** ePlant TreeTag battery voltage. */
    ePlantTreeTag_D_battery: fixedPortTemplate(true, true, "EPlantTreeTag_Battery"),

    // ── Davis WeatherLink station ──────────────────────────────────────────────

    /** Davis WeatherLink station's own console/gateway battery. */
    davisWeatherLink_D_consoleBattery: fixedPortTemplate(true, true, "Davis_WeatherLink_Battery"),
    /** Davis WeatherLink ISS transmitter's own battery — independent of the console battery above. */
    davisWeatherLink_D_transmitterBattery: fixedPortTemplate(true, true, "Davis_WeatherLink_TransmitterBattery"),

    // ── Zone virtual ports (not yet assigned to any zone config) ──────────────

    /** Zone NDVI (avg + variance). */
    zone_V_ndvi: fixedPortTemplate(true, false, "Zone_NDVI"),
    /** Zone precipitation and rainfall. */
    zone_V_precipitation: fixedPortTemplate(true, false, "Zone_Precipitation"),
    /** Zone percentage of valves currently open. */
    zone_V_valveOpenPercent: fixedPortTemplate(true, false, "Zone_ValveOpenPercent"),
    /** Zone percentage of valves currently online. */
    zone_V_valveOnlinePercent: fixedPortTemplate(true, false, "Zone_ValveOnlinePercent"),
    /** Zone irrigation schedule status. */
    zone_V_zoneIrrigationScheduled: fixedPortTemplate(true, false, "Zone_ZoneIrrigationScheduled"),
} as const;
